import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const getContentWidth = (element) => {
  if (!element) return 0;

  const computedStyle = window.getComputedStyle(element);
  const paddingLeft = Number.parseFloat(computedStyle.paddingLeft) || 0;
  const paddingRight = Number.parseFloat(computedStyle.paddingRight) || 0;

  return Math.max(0, element.clientWidth - paddingLeft - paddingRight);
};

const AutoFitText = ({
  children,
  minPx = 12,
  maxPx = 48,
  className = "",
  style = {},
  textClassName = ""
}) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [fontSize, setFontSize] = useState(maxPx);
  const fontSizeRef = useRef(maxPx);
  const lastWidthRef = useRef(0);
  const rafRef = useRef(0);

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const text = textRef.current;
    if (!container || !text) return;

    const availableWidth = getContentWidth(container);
    if (!availableWidth) return;

    let low = minPx;
    let high = maxPx;
    let best = minPx;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      text.style.fontSize = `${mid}px`;
      text.style.lineHeight = "1.1";

      const fits = text.scrollWidth <= availableWidth;
      if (fits) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    if (best !== fontSizeRef.current) {
      fontSizeRef.current = best;
      setFontSize(best);
    }
  }, [minPx, maxPx]);

  useLayoutEffect(() => {
    recompute();
  }, [children, recompute]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver((entries) => {
      const entry = entries && entries[0];
      const nextWidth = entry?.contentRect?.width ?? container.clientWidth;
      // Avoid ResizeObserver feedback loops: only recompute when width changes.
      if (!nextWidth || Math.abs(nextWidth - lastWidthRef.current) < 0.5) return;
      lastWidthRef.current = nextWidth;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        recompute();
      });
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, [recompute]);

  return (
    <div
      ref={containerRef}
      className={`w-full overflow-hidden ${className}`}
      style={style}
    >
      <span
        ref={textRef}
        className={textClassName}
        style={{
          fontSize: `${fontSize}px`,
          lineHeight: 1.1,
          whiteSpace: "nowrap",
          display: "inline-block"
        }}
      >
        {children}
      </span>
    </div>
  );
};

export default AutoFitText;
